import { useState } from 'react';
import {
  Button, Checkbox, makeStyles, tokens,
  Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions,
} from '@fluentui/react-components';
import { Filter24Regular, Dismiss24Regular } from '@fluentui/react-icons';
import { DateRangeFilterModal, type DateRangeValue } from './DateRangeFilterModal';
import { useT } from '../../i18n';

export interface FilterOption {
  value: string;
  label: string;
}

export interface FilterDimension {
  key: string;
  label: string;
  options: FilterOption[];
  selected: string[];
  onApply: (values: string[]) => void;
  onClear: () => void;
}

export interface DateRangeDimension {
  label: string;
  value: DateRangeValue;
  onApply: (value: DateRangeValue) => void;
  onClear: () => void;
}

interface FilterPanelProps {
  /** Dimensiones tipo checkbox (Tipo, Usuario, etc.) — el caller ya resuelve
   * las opciones y el estado, este componente no conoce el dominio. */
  dimensions: FilterDimension[];
  /** Dimensión especial de rango de fecha, opcional. */
  dateRange?: DateRangeDimension;
  onClearAll: () => void;
}

const useStyles = makeStyles({
  bar: { display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' },
  chipWrap: { position: 'relative' },
  dot: {
    position: 'absolute', top: '-2px', right: '-2px',
    width: '8px', height: '8px', borderRadius: '50%',
    backgroundColor: tokens.colorBrandBackground,
    pointerEvents: 'none',
  },
  checkboxList: {
    display: 'flex', flexDirection: 'column', gap: '4px',
    maxHeight: '280px', overflowY: 'auto', paddingTop: '8px',
  },
});

function CheckboxDimensionModal({
  open, dimension, onClose,
}: { open: boolean; dimension: FilterDimension; onClose: () => void }) {
  const s = useStyles();
  const t = useT();
  const [draft, setDraft] = useState<string[]>(dimension.selected);

  const allSelected = draft.length === dimension.options.length && dimension.options.length > 0;
  const toggleAll = () => setDraft(allSelected ? [] : dimension.options.map(o => o.value));
  const toggleOne = (value: string) =>
    setDraft(d => d.includes(value) ? d.filter(v => v !== value) : [...d, value]);

  const handleApply = () => { dimension.onApply(draft); onClose(); };
  const handleClear = () => { setDraft([]); dimension.onClear(); onClose(); };

  return (
    <Dialog open={open} onOpenChange={(_, d) => !d.open && onClose()} modalType="modal">
      <DialogSurface style={{ maxWidth: '360px' }}>
        <DialogBody>
          <DialogTitle>{dimension.label}</DialogTitle>
          <DialogContent>
            <div className={s.checkboxList}>
              <Checkbox
                label={t.filterPanel.selectAll}
                checked={allSelected}
                onChange={toggleAll}
              />
              {dimension.options.map(opt => (
                <Checkbox
                  key={opt.value}
                  label={opt.label}
                  checked={draft.includes(opt.value)}
                  onChange={() => toggleOne(opt.value)}
                />
              ))}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="subtle" size="small" onClick={handleClear}>{t.filterPanel.clear}</Button>
            <Button appearance="primary" size="small" onClick={handleApply}>
              {t.filterPanel.apply}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

/**
 * Panel de filtros multi-dimensión reusable — desacoplado de cualquier
 * concepto de dominio (actividad, lote, producto). Cada dimensión abre su
 * propio modal de checkboxes; la dimensión de fecha (opcional) abre un modal
 * de rango. Ver docs/superpowers/specs/2026-09-12-filterpanel-vista-lote-design.md.
 */
export function FilterPanel({ dimensions, dateRange, onClearAll }: FilterPanelProps) {
  const s = useStyles();
  const t = useT();
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [dateOpen, setDateOpen] = useState(false);

  const hasAnyActive =
    dimensions.some(d => d.selected.length > 0) ||
    !!(dateRange && (dateRange.value.from || dateRange.value.to));

  return (
    <div className={s.bar}>
      <Filter24Regular />
      {dateRange && (
        <div className={s.chipWrap}>
          <Button appearance="outline" size="small" onClick={() => setDateOpen(true)}>
            {dateRange.label}
          </Button>
          {(dateRange.value.from || dateRange.value.to) && <span className={s.dot} />}
        </div>
      )}
      {dimensions.map(dim => (
        <div key={dim.key} className={s.chipWrap}>
          <Button appearance="outline" size="small" onClick={() => setOpenKey(dim.key)}>
            {dim.label}
          </Button>
          {dim.selected.length > 0 && <span className={s.dot} />}
        </div>
      ))}
      {hasAnyActive && (
        <Button
          appearance="subtle"
          size="small"
          icon={<Dismiss24Regular />}
          onClick={onClearAll}
        >
          {t.filterPanel.clearAll}
        </Button>
      )}

      {dateRange && (
        <DateRangeFilterModal
          open={dateOpen}
          label={dateRange.label}
          fromLabel={t.filterPanel.dateFrom}
          toLabel={t.filterPanel.dateTo}
          applyLabel={t.filterPanel.apply}
          clearLabel={t.filterPanel.clear}
          value={dateRange.value}
          onApply={dateRange.onApply}
          onClear={dateRange.onClear}
          onClose={() => setDateOpen(false)}
        />
      )}

      {dimensions.map(dim => (
        <CheckboxDimensionModal
          key={dim.key}
          open={openKey === dim.key}
          dimension={dim}
          onClose={() => setOpenKey(null)}
        />
      ))}
    </div>
  );
}
